<template>
  <div
    @click.self="close"
    v-if="character && character.owner"
    :class="`f-${theme}`"
    class="opened-character-container"
    role="dialog"
    aria-modal="true"
    :aria-label="character.name">
    <!-- Mobile-only (styled in game/mobile.scss): the stacked layout
         leaves little backdrop to tap, so give an explicit close. -->
    <button
      type="button"
      :aria-label="$t('a11y.agent.close_panel')"
      @click="close"
      class="system-close-button">
      <svgicon
        name="close"
        aria-hidden="true" />
    </button>

    <div class="opened-character">
      <div class="opened-character-owner">
        <span v-html="$tmd('galaxy.opened_character.commanded_by', {characterName: character.owner.name})"/>
        <hr />
        <span v-html="$tmd('galaxy.opened_character.character_faction', {faction: character.owner.faction})"/>
        <template v-if="canReport">
          <hr />
          <button
            type="button"
            class="chat-report-button"
            v-tooltip="$t('in_game_chat.sighting.report_agent_hint')"
            @click="report">
            {{ $t('in_game_chat.sighting.report_agent') }}
          </button>
        </template>
      </div>

      <div class="opened-character-card">
        <character-card
          v-if="character"
          ref="card"
          @deactivated="deactivateCharacter"
          :open="true"
          :character="character"
          :theme="theme"
          :lock="true" />
      </div>

      <div
        v-if="character.status === 'on_board'"
        class="opened-character-aside">
        <army
          v-if="character.type === 'admiral'"
          :theme="theme"
          :valign="'top'"
          :halign="'right'"
          :context="'display'"
          :character="character" />

        <spy
          v-if="character.type === 'spy'"
          :character="character" />

        <speaker
          v-if="character.type === 'speaker'"
          :character="character" />
      </div>

      <!-- a student has no orders: beside its card, what its course gives
           and costs, and when (docs/agent-training.md) -->
      <div
        v-else-if="isTraining"
        class="opened-character-aside is-training">
        <training-queue
          :character="character"
          :theme="theme"
          @over="refresh" />
      </div>
    </div>
  </div>
</template>

<script>
import CharacterCard from '@/game/components/card/CharacterCard.vue';
import Army from '@/game/components/galaxy/selection/Army.vue';
import Spy from '@/game/components/galaxy/selection/Spy.vue';
import Speaker from '@/game/components/galaxy/selection/Speaker.vue';
import TrainingQueue from '@/game/components/galaxy/selection/TrainingQueue.vue';
import { inClass } from '@/game/training';
import { canReportAgent, reportAgent } from '@/game/components/chat/reportSighting';

export default {
  name: 'opened-character',
  computed: {
    character() { return this.$store.state.game.openedCharacter; },
    // Another faction's agent standing in a system: it can be reported
    // to the faction's Spotted chat channel.
    canReport() {
      return canReportAgent(this, this.character)
        && this.character.status === 'on_board'
        && this.character.system != null;
    },
    theme() {
      return this.character?.owner && this.$store.getters['game/themeByKey'](this.character.owner.faction);
    },
    isTraining() {
      return this.character.status === 'student' && inClass(this.character.training);
    },
  },
  watch: {
    // Opening reads the card out; closing (Esc via Game.vue, the
    // backdrop, the button) returns focus to where it was.
    'character.id': function onOpenedChanged(id, previousId) {
      if (id && !previousId) this.returnFocus = document.activeElement;
      if (id) {
        this.$nextTick(() => {
          if (this.$refs.card) this.$refs.card.focusSummary();
        });
      } else {
        const target = this.returnFocus;
        this.returnFocus = null;
        if (target && document.body.contains(target) && target.focus) target.focus();
      }
    },
  },
  methods: {
    close() {
      this.$store.dispatch('game/closeCharacter');
    },
    // The opened card is a copy taken when it opened. When its student is
    // done settling in, take a new one (a moment later: the server ticks
    // the agent at that very time).
    refresh() {
      const { id } = this.character;

      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        if (this.character && this.character.id === id) {
          this.$store.dispatch('game/openCharacter', { vm: this, id });
        }
      }, 1500);
    },
    // The card steps aside: the chat opens on the report behind it.
    report() {
      reportAgent(this, this.character.system, this.character.id);
      this.close();
    },
    deactivateCharacter() {
      this.$store.dispatch('game/closeCharacter');
    },
  },
  beforeDestroy() {
    clearTimeout(this.refreshTimer);
  },
  components: {
    CharacterCard,
    Army,
    Spy,
    Speaker,
    TrainingQueue,
  },
};
</script>
